// Translated from beebjit by Chris Evans.
// https://github.com/scarybeasts/beebjit
// eslint-disable-next-line no-unused-vars
import { Scheduler } from "./scheduler.js";
// eslint-disable-next-line no-unused-vars
import { Disc } from "./disc.js";
import { IbmDiscFormat } from "./disc.js";

class StepEvent extends Event {
    constructor(stepAmount) {
        super("step");
        this.stepAmount = stepAmount;
    }
}

/**
 * Abstract base class defining the interface for disc drives.
 * All disc drive implementations must extend this class.
 */
export class BaseDiscDrive extends EventTarget {
    /** @returns {Disc|undefined} */
    get disc() {
        throw new Error("Not implemented: disc getter");
    }

    /** @returns {number} */
    get track() {
        throw new Error("Not implemented: track getter");
    }

    /** @returns {number} */
    get headPosition() {
        throw new Error("Not implemented: headPosition getter");
    }

    /** @returns {boolean} */
    get isSideUpper() {
        throw new Error("Not implemented: isSideUpper getter");
    }

    /** @returns {Number} */
    get tracksPerStep() {
        throw new Error("Not implemented: tracksPerStep getter");
    }

    /** @param {Number} _tracksPerStep */
    set tracksPerStep(_tracksPerStep) {
        throw new Error("Not implemented: tracksPerStep setter");
    }

    /** @returns {boolean} */
    get indexPulse() {
        throw new Error("Not implemented: indexPulse getter");
    }

    /** @returns {boolean} */
    get spinning() {
        throw new Error("Not implemented: spinning getter");
    }

    /** @returns {boolean} */
    get writeProtect() {
        throw new Error("Not implemented: writeProtect getter");
    }

    /** @returns {number} */
    get trackLength() {
        throw new Error("Not implemented: trackLength getter");
    }

    /** @returns {number} */
    get positionFraction() {
        throw new Error("Not implemented: positionFraction getter");
    }

    /** @returns {number} */
    get positionTime() {
        throw new Error("Not implemented: positionTime getter");
    }

    /**
     * @param {Disc|undefined} _disc
     */
    setDisc(_disc) {
        throw new Error("Not implemented: setDisc");
    }

    /**
     * @param {function(number, number): void} _callback
     */
    setPulsesCallback(_callback) {
        throw new Error("Not implemented: setPulsesCallback");
    }

    startSpinning() {
        throw new Error("Not implemented: startSpinning");
    }

    stopSpinning() {
        throw new Error("Not implemented: stopSpinning");
    }

    /**
     * @param {boolean} _isSideUpper
     */
    selectSide(_isSideUpper) {
        throw new Error("Not implemented: selectSide");
    }

    /**
     * @param {number} _delta
     */
    seekOneTrack(_delta) {
        throw new Error("Not implemented: seekOneTrack");
    }

    /**
     * @param {number} _newTrack
     */
    notifySeek(_newTrack) {
        throw new Error("Not implemented: notifySeek");
    }

    /**
     * @param {number} _delta
     */
    notifySeekAmount(_delta) {
        throw new Error("Not implemented: notifySeekAmount");
    }

    /**
     * @param {boolean} _isDoubleDensity
     */
    set32usMode(_isDoubleDensity) {
        throw new Error("Not implemented: set32usMode");
    }

    /**
     * @param {number} _pulses
     */
    writePulses(_pulses) {
        throw new Error("Not implemented: writePulses");
    }

    /** @returns {number} */
    getQuasiRandomPulses() {
        throw new Error("Not implemented: getQuasiRandomPulses");
    }
}

export class DiscDrive extends BaseDiscDrive {
    static get TicksPerRevolution() {
        // 300 rpm
        return 400000;
    }

    // scarybeast's Chinon drive holds the index pulse low for about 4ms. */
    static get DiscIndexMs() {
        return 4;
    }

    /**
     * Create a new DiscDrive.
     *
     * @param {Number} id which drive id this is (0 or 1)
     * @param {Scheduler} scheduler scheduler to register callbacks etc
     */
    constructor(id, scheduler) {
        super();
        this._scheduler = scheduler;
        /** @type {Disc|undefined} */
        this._disc = undefined;
        // Two for a drive whose 40/80 switch is set to 40, which reaches a 48 tpi format by
        // stepping twice for each track the controller counts.
        this._tracksPerStep = 1;
        // Where the head is over the 96 tpi surface, whatever the controller believes.
        this._track = 0;
        this._isSideUpper = false;
        // In units where 3125 is a normal track length.
        this._headPosition = 0;
        // Extra precision for head position, needed for MFM.
        this._pulsePosition = 0;
        this._in32usMode = false;
        /** @type {function(number, number): void} */
        this._pulsesCallback = null;

        this._timer = this._scheduler.newTask(this._onTimer.bind(this));
        this._spinning = false;
    }

    /**
     * @returns {Disc|undefined}
     */
    get disc() {
        return this._disc;
    }

    getQuasiRandomPulses() {
        const ticks = this._scheduler.epoch;
        const fmData = (ticks ^ (ticks >>> 8) ^ (ticks >>> 16) ^ (ticks >>> 24)) & 0xff;
        return IbmDiscFormat.fmTo2usPulses(0xff, fmData);
    }

    get trackLength() {
        const disc = this.disc;
        if (!disc) return IbmDiscFormat.bytesPerTrack;
        return disc.getTrack(this._isSideUpper, this._track).length;
    }

    _onTimer() {
        let pulses = this.disc ? this.disc.readPulses(this._isSideUpper, this._track, this._headPosition) : 0;
        let numPulses = 32;
        if (this._pulsePosition === 16 || this._in32usMode) {
            numPulses = 16;
            if (this._pulsePosition === 0) pulses >>>= 16;
            pulses &= 0xffff;
        }

        // If there's an empty patch on the disc surface, the disc drive's head amplifier will typically desperately
        // seek for a signal in the noise, resulting in "weak bits". @scarybeasts verified this with an oscilloscope on
        // his Chinon F-051MD drive, which has a Motorola MC3470AP head amplifier. We need to return an inconsistent yet
        // deterministic set of weak bits.
        if (pulses === 0) pulses = this.getQuasiRandomPulses();

        if (this._pulsesCallback) {
            this._pulsesCallback(pulses, numPulses);
        }

        const thisTicks = this.positionTime;

        // Advance head position.
        if (numPulses === 16) {
            if (this._pulsePosition === 0) {
                this._pulsePosition = 16;
            } else {
                this._pulsePosition = 0;
                this._headPosition++;
            }
        } else {
            this._headPosition++;
        }

        const nextTicks = this.positionTime;

        if (this._headPosition === this.trackLength) {
            this._headPosition = 0;
            this._checkTrackNeedsWrite();
        }
        if (this._spinning) this._timer.reschedule(nextTicks - thisTicks);
    }

    get headPosition() {
        return this._headPosition;
    }

    get track() {
        return this._track;
    }

    get isSideUpper() {
        return this._isSideUpper;
    }

    get positionFraction() {
        return (this._headPosition + this._pulsePosition / 32) / this.trackLength;
    }

    set positionFraction(fraction) {
        this._headPosition = (this.trackLength * fraction) | 0;
        this._pulsePosition = 0;
    }

    get positionTime() {
        return (this.positionFraction * DiscDrive.TicksPerRevolution) | 0;
    }

    /**
     * @param {function(number, number): void} callback
     */
    setPulsesCallback(callback) {
        this._pulsesCallback = callback;
    }

    get spinning() {
        // beebjit uses the timer's scheduledness here, but our schedule system deschedules timers
        // during callbacks, which makes this briefly "false" and disturbs things.
        return this._spinning;
    }

    startSpinning() {
        if (!this._spinning) {
            this.dispatchEvent(new Event("startSpinning"));
            this._timer.schedule(1);
        }
        this._spinning = true;
    }

    stopSpinning() {
        if (this._spinning) {
            this.dispatchEvent(new Event("stopSpinning"));
        }
        this._timer.cancel();
        this._spinning = false;
    }

    selectSide(isSideUpper) {
        const fraction = this.positionFraction;
        this._checkTrackNeedsWrite();
        this._isSideUpper = isSideUpper;
        this.positionFraction = fraction;
    }

    /**
     * @param {Disc} disc
     */
    setDisc(disc) {
        this._disc = disc;
    }

    /** @returns {Number} how many of the surface's tracks the head crosses for one of the format's */
    get tracksPerStep() {
        return this._tracksPerStep;
    }

    set tracksPerStep(tracksPerStep) {
        if (tracksPerStep !== 1 && tracksPerStep !== 2)
            throw new Error(`Drives step over one or two tracks at a time, not ${tracksPerStep}`);
        this._tracksPerStep = tracksPerStep;
    }

    /** @returns {Number} the track the controller believes the head is on */
    get logicalTrack() {
        return (this._track / this._tracksPerStep) | 0;
    }

    get indexPulse() {
        // With no disc loaded the drive asserts the index all the time.
        if (!this.disc) return true;
        // The 8271 datasheet says that the index pulse must be held for over 0.5us. Most drives are in the millisecond range.
        return this._headPosition < (this.trackLength * DiscDrive.DiscIndexMs) / 200;
    }

    writePulses(pulses) {
        if (!this.disc) return;
        // All drives seen have a write-protect failsafe on the drive itself.
        if (this.disc.writeProtected) return;
        if (this._in32usMode) {
            if (pulses & 0xffff0000) throw new Error(`Unable to write 32us pulses for ${pulses}`);
            const existingPulses = this.disc.readPulses(this._isSideUpper, this.track, this.headPosition);
            if (this._pulsePosition === 0) pulses = (existingPulses & 0x0000ffff) | (pulses << 16);
            else pulses = (existingPulses & 0xffff0000) | pulses;
        }
        this.disc.writePulses(this._isSideUpper, this.track, this.headPosition, pulses);
    }

    get writeProtect() {
        return this.disc ? this.disc.writeProtected : false;
    }

    set32usMode(isDoubleDensity) {
        this._in32usMode = isDoubleDensity;
    }

    /**
     * Seek a relative track.
     *
     * @param {Number} delta track step delta, either 1 or -1
     */
    seekOneTrack(delta) {
        this._selectTrack(this._track + delta * this._tracksPerStep);
    }

    /**
     * Notify that an overall seek is happening to a particular track. Purely informational.
     */
    notifySeek(newTrack) {
        this.notifySeekAmount(newTrack - this.logicalTrack);
    }

    /**
     * Notify that an overall seek is happening by some delta amount. Purely informational.
     */
    notifySeekAmount(delta) {
        // The step drives the seek noise, so it counts the tracks the head crosses.
        this.dispatchEvent(new StepEvent(delta * this._tracksPerStep));
    }

    /**
     * @param {Number} track
     */
    _selectTrack(track) {
        this._checkTrackNeedsWrite();
        const lastTrack = IbmDiscFormat.tracksPerDisc - this._tracksPerStep;
        if (track < 0) {
            track = 0;
            console.log("Clang! disc head stopped at track 0");
        } else if (track > lastTrack) {
            track = lastTrack;
            console.log("Clang! disc head stopper at track max");
        }
        const fraction = this.positionFraction;
        this._track = track;
        this.positionFraction = fraction;
    }

    _checkTrackNeedsWrite() {
        if (!this.disc) return;
        const written = this.disc.flushWrites();
        // A 48 tpi head writes across most of its band but not as far as the neighbouring 96 tpi
        // track, which is left in the guard band with nothing readable on it.
        if (written && this._tracksPerStep === 2) this.disc.eraseTrack(written.isSideUpper, written.trackNum ^ 1);
    }

    snapshotState() {
        return {
            track: this._track,
            isSideUpper: this._isSideUpper,
            headPosition: this._headPosition,
            pulsePosition: this._pulsePosition,
            in32usMode: this._in32usMode,
            spinning: this._spinning,
            is40Track: this._tracksPerStep === 2,
            timerTaskOffset: this._timer.scheduled() ? this._timer.expireEpoch - this._scheduler.epoch : null,
            disc: this._disc ? this._disc.snapshotState() : null,
        };
    }

    restoreState(state) {
        this._track = state.track;
        this._isSideUpper = state.isSideUpper;
        this._headPosition = state.headPosition;
        this._pulsePosition = state.pulsePosition;
        this._in32usMode = state.in32usMode;
        this._tracksPerStep = state.is40Track ? 2 : 1;

        // Restore spinning state and timer
        this._timer.cancel();
        this._spinning = state.spinning;
        if (state.timerTaskOffset !== null) this._timer.schedule(state.timerTaskOffset);

        // Restore disc data if present
        if (state.disc && this._disc) {
            this._disc.restoreState(state.disc);
        }
    }
}
